
import { sanitize } from 'sanitize-filename-ts';
import {
  createSlice,
  createAsyncThunk,
  PayloadAction
} from '@reduxjs/toolkit';

import { DownloadPayload, DownloadState } from 'libs/redux-store/src/download-rates/types';
import axios from 'axios';
const initialState: DownloadState = {
  loading: false,
  error: null,
  success: null,
};
export const parseDownloadError = async (error: any): Promise<string> => {
  if (error.response && error.response.data instanceof Blob) {
    try {
      const errorText = await error.response.data.text();
      const errorJson = JSON.parse(errorText);
      return errorJson.error || 'Unknown error occurred.';
    } catch {
      return 'Failed to parse error message.';
    }
  }
  return error.message || 'An unexpected error occurred.';
};
export const downloadCSV = createAsyncThunk<void, DownloadPayload, { rejectValue: string }>(
  'rateDownload/downloadCSV',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/product/rates/download', {
        params: payload,
        responseType: 'blob',
      });
      if (!response.data || response.data.size === 0) {
        return rejectWithValue('No data available to download.');
      }
      const disposition = response.headers['content-disposition'];
      let fileName = 'rates.csv';
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^\"]+)"?/);
        if (match?.[1]) {
          fileName = sanitize(match[1]) || 'rates.csv';
        }
      }
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      link.click();
      window.URL.revokeObjectURL(url);
      return undefined;
    } catch (error: any) {
      const parsedError = await parseDownloadError(error);
      return rejectWithValue(parsedError);
    }
  }
);
const downloadRatesSlice = createSlice({
  name: 'downloadRates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(downloadCSV.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(downloadCSV.fulfilled, (state) => {
        state.loading = false;
        state.success = 'Download completed.';
        state.error = null;
      })
      .addCase(downloadCSV.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
        state.success = null;
      });
  },
});
export default downloadRatesSlice.reducer;